import React, { useMemo, useState } from 'react';

import { ConfirmModal, MultiSelect, Text } from '../../../../components';
import { languages, supportedLanguages } from '../../../../utilities/languages';
import { languagePatternsHaveContent } from '../../utils/voicemailDetectionPatterns';
import { LanguageBlock } from './LanguageBlock';
import type { VoicemailDetectionPatternsEditorProps } from './types';

export type { VoicemailDetectionPatternsEditorProps };

export const VoicemailDetectionPatternsEditor: React.FC<
  VoicemailDetectionPatternsEditorProps
> = ({ value = {}, onChange, disabled }) => {
  const [pendingRemoveLang, setPendingRemoveLang] = useState<string | null>(
    null
  );

  const selectedLangCodes = Object.keys(value);

  const multiSelectOptions = useMemo((): Array<{
    value: string;
    label: string;
  }> => {
    const supportedSet = new Set<string>(
      supportedLanguages.map(l => String(l.value))
    );
    const allLangLabelMap = new Map<string, string>(
      languages.map(l => [
        String(l.value),
        typeof l.label === 'string' ? l.label : String(l.value),
      ])
    );

    const base: Array<{ value: string; label: string }> =
      supportedLanguages.map(l => ({
        value: String(l.value),
        label: typeof l.label === 'string' ? l.label : String(l.value),
      }));

    const syntheticOptions = selectedLangCodes
      .filter(code => !supportedSet.has(code))
      .map(code => ({
        value: code,
        label: allLangLabelMap.get(code) ?? code,
      }));

    return [...base, ...syntheticOptions];
  }, [selectedLangCodes]);

  const getLangLabel = (code: string): string =>
    multiSelectOptions.find(o => o.value === code)?.label ?? code;

  const handleLanguageChange = (newCodes: string[]) => {
    const addedCode = newCodes.find(c => !selectedLangCodes.includes(c));
    if (addedCode) {
      onChange?.({ ...value, [addedCode]: {} });
    }
  };

  const handleBeforeRemove = (
    option: { value: string; label: string },
    _nextValues: string[]
  ): false | void => {
    const langCode = option.value;
    if (languagePatternsHaveContent(value[langCode])) {
      setPendingRemoveLang(langCode);
      return false;
    }
    const { [langCode]: _removed, ...rest } = value;
    onChange?.(rest);
  };

  const confirmRemoveLang = () => {
    if (!pendingRemoveLang) return;
    const { [pendingRemoveLang]: _removed, ...rest } = value;
    onChange?.(rest);
    setPendingRemoveLang(null);
  };

  const updateLanguage = (
    langCode: string,
    tiers: Record<string, string[]>
  ) => {
    onChange?.({ ...value, [langCode]: tiers });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <MultiSelect
        options={multiSelectOptions}
        value={selectedLangCodes}
        onChange={handleLanguageChange}
        onBeforeRemove={handleBeforeRemove}
      />

      {selectedLangCodes.length > 0 && (
        <div className="flex flex-col gap-3">
          {selectedLangCodes.map(langCode => (
            <LanguageBlock
              key={langCode}
              langCode={langCode}
              langLabel={getLangLabel(langCode)}
              tiers={value[langCode] ?? {}}
              disabled={disabled}
              onChange={tiers => updateLanguage(langCode, tiers)}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        open={pendingRemoveLang !== null}
        title="Remove language patterns"
        onClose={() => setPendingRemoveLang(null)}
        onConfirm={confirmRemoveLang}
        confirmButtonClassName="w-full bg-dangerSecondary hover:bg-danger border border-dangerSecondary text-contrast"
        confirmButtonLabel="Delete"
      >
        <div className="flex flex-col gap-y-md justify-center items-center mt-md">
          <Text className="text-center text-3xl">
            This language has configured patterns. Removing it will delete all
            its tiers and patterns. This action cannot be undone.
          </Text>
        </div>
      </ConfirmModal>
    </div>
  );
};
